/* EXPORT (owner call 2026-09-19: "the same eclipse menu as ink… export"): turn
   the CURRENT word into a file. Markdown (portable, lossless for prose), a
   styled self-contained HTML page, or PDF through the browser's print dialog.
   Ink's own export, cut down to a text-only body — no dependencies, a Blob
   download and window.print. */

const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const slug = t => (t || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'untitled'

export function fileName(title, ext) { return `${slug(title)}.${ext}` }

export function download(name, content, mime) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/* the body, without the word: the editor's first block is the title, and if
   the stored HTML still carries it as an <h1> it must not be exported twice */
function bodyOf(word) {
  return String(word.body || '').replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>/i, '').trim()
}

/* HTML → Markdown for the shapes a text-only definition can hold: paragraphs,
   headings, emphasis, links, lists, inline code, line breaks. Anything else
   loses its tag and keeps its words. */
export function htmlToMd(html) {
  let s = String(html || '')
  s = s.replace(/<br\s*\/?>/gi, '\n')
  s = s.replace(/<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi, (_, n, t) => `\n${'#'.repeat(+n)} ${t.trim()}\n\n`)
  s = s.replace(/<(strong|b)>([\s\S]*?)<\/\1>/gi, '**$2**')
  s = s.replace(/<(em|i)>([\s\S]*?)<\/\1>/gi, '_$2_')
  s = s.replace(/<code>([\s\S]*?)<\/code>/gi, '`$1`')
  s = s.replace(/<a [^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
  s = s.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => { let i = 0; return '\n' + inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, t) => `${++i}. ${t.trim()}\n`) + '\n' })
  s = s.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner) => '\n' + inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, t) => `- ${t.trim()}\n`) + '\n')
  s = s.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (_, t) => `${t.trim()}\n\n`)
  s = s.replace(/<[^>]+>/g, '')
  s = s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
  return s.replace(/\n{3,}/g, '\n\n').trim()
}

/* the facts about the word, for a frontmatter or a meta line */
function facts(word) {
  const f = []
  if (word.tags?.length) f.push(['tags', word.tags])
  if (word.aliases?.length) f.push(['aliases', word.aliases])
  if (word.synonyms?.length) f.push(['synonyms', word.synonyms.map(r => r.term)])
  if (word.antonyms?.length) f.push(['antonyms', word.antonyms.map(r => r.term)])
  if (word.status) f.push(['status', word.status])
  // an anti-word says so, and names what to say instead (the anti-dictionary)
  if (word.stance === 'avoided') f.push(['stance', 'avoided'])
  if (word.stance === 'avoided' && word.rather?.length) f.push(['rather', word.rather.map(r => r.term)])
  if (word.committedAt) f.push(['committed', String(word.committedAt).slice(0, 10)])
  return f
}

/* ── THE REFERENCES (owner call 2026-09-19: "obsidian style reference
   aliasing … so it's all consistent"): in a Markdown file, every phrase the
   editor would mark as a defined word is written as a reference to it —
   `[title|phrase]` when the phrase is one of the word's other names,
   `[title]` when it is the title itself. SINGLE brackets, on purpose: `[[…]]`
   names a NOTE in ink, and a definition is another kind of thing. The
   matching is the editor's own rule (defined-terms.js): exact, case-blind,
   on word boundaries, longest needle first, never the word's own page.
   Code spans and existing links are left as they are. */
const BOUNDARY = /[\p{L}\p{N}_]/u
const isEdge = (s, i) => i < 0 || i >= s.length || !BOUNDARY.test(s[i])
export function termNeedles(terms) {
  const out = []
  for (const t of terms || []) {
    const title = String(t?.term || t?.name || '').trim()
    if (!title) continue
    for (const raw of [title, ...(t.aliases || [])]) {
      const needle = String(raw || '').trim().toLowerCase()
      if (needle) out.push({ needle, id: t.id, title })
    }
  }
  return out.sort((a, b) => b.needle.length - a.needle.length)
}
function linkPlain(text, needles) {
  const lower = text.toLowerCase()
  const taken = new Array(text.length).fill(false)
  const hits = []
  for (const n of needles) {
    let at = lower.indexOf(n.needle)
    while (at !== -1) {
      const end = at + n.needle.length
      if (isEdge(lower, at - 1) && isEdge(lower, end) && !taken.slice(at, end).some(Boolean)) {
        for (let i = at; i < end; i += 1) taken[i] = true
        hits.push({ at, end, title: n.title })
      }
      at = lower.indexOf(n.needle, at + 1)
    }
  }
  if (!hits.length) return text
  hits.sort((a, b) => a.at - b.at)
  let out = '', pos = 0
  for (const h of hits) {
    const phrase = text.slice(h.at, h.end)
    out += text.slice(pos, h.at) + (phrase.toLowerCase() === h.title.toLowerCase() ? `[${h.title}]` : `[${h.title}|${phrase}]`)
    pos = h.end
  }
  return out + text.slice(pos)
}
export function linkTerms(md, terms, { selfId = null } = {}) {
  const needles = termNeedles((terms || []).filter(t => !selfId || t.id !== selfId))
  if (!needles.length) return md
  // the segments to leave alone: code spans, [[note links]], [text](url), [text][ref]
  const skip = /`[^`]*`|\[\[[^\]]*\]\]|\[[^\]]*\]\([^)]*\)|\[[^\]]*\]\[[^\]]*\]/g
  let out = '', pos = 0, m
  while ((m = skip.exec(md))) { out += linkPlain(md.slice(pos, m.index), needles) + m[0]; pos = m.index + m[0].length }
  return out + linkPlain(md.slice(pos), needles)
}

export function buildMarkdown(word, { meta = true, terms = null } = {}) {
  const name = word.name || 'untitled'
  let out = ''
  if (meta) {
    out += '---\n'
    out += `word: ${name}\n`
    for (const [k, v] of facts(word)) out += Array.isArray(v) ? `${k}: [${v.join(', ')}]\n` : `${k}: ${v}\n`
    out += '---\n\n'
  }
  const prose = htmlToMd(bodyOf(word)) || word.text || ''
  out += `# ${name}\n\n${terms ? linkTerms(prose, terms, { selfId: word.id }) : prose}\n`
  return out
}

const EXPORT_CSS = `
  :root { color-scheme: light; }
  body { margin: 0; background: #f6f6f4; color: #1a1a1a; font-family: Georgia, 'Times New Roman', serif; line-height: 1.65; }
  .dt-export { max-width: 700px; margin: 0 auto; padding: 64px 24px 96px; }
  .dt-export__word { font-family: -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 2.6rem; line-height: 1.05; letter-spacing: -.02em; margin: 0 0 .35em; font-weight: 600; }
  .dt-export__meta { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: .78rem; letter-spacing: .04em; text-transform: uppercase; color: #888; margin: 0 0 2em; padding-bottom: 1.2em; border-bottom: 1px solid #e2e2dd; }
  .dt-export__body p { margin: 0 0 1em; }
  .dt-export__body a { color: #7a5c12; }
  .dt-export__body h1, .dt-export__body h2, .dt-export__body h3 { font-weight: 600; line-height: 1.35; margin: 1.4em 0 .5em; }
  .dt-export__body code { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: .85em; background: rgba(0,0,0,.06); padding: .1em .3em; border-radius: 3px; }
  .dt-export__body ul, .dt-export__body ol { padding-left: 1.4em; }
  @media print { body { background: #fff; } .dt-export { padding: 0; max-width: none; } }
`

export function buildHTML(word, { meta = true } = {}) {
  const name = word.name || 'untitled'
  const bits = facts(word).map(([k, v]) => k === 'tags' ? v.map(t => '#' + esc(t)).join(' ') : k === 'aliases' ? `also ${v.map(esc).join(', ')}` : k === 'synonyms' ? `like ${v.map(esc).join(', ')}` : k === 'antonyms' ? `not ${v.map(esc).join(', ')}` : k === 'rather' ? `rather ${v.map(esc).join(', ')}` : esc(v))
  const body = bodyOf(word) || `<p>${esc(word.text || '')}</p>`
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(name)}</title>
<style>${EXPORT_CSS}</style></head>
<body><article class="dt-export">
<h1 class="dt-export__word">${esc(name)}</h1>
${meta && bits.length ? `<p class="dt-export__meta">${bits.join(' &middot; ')}</p>` : ''}
<div class="dt-export__body">${body}</div>
</article></body></html>`
}

export function printHTML(html) {
  const w = window.open('', '_blank')
  if (!w) return
  w.document.open(); w.document.write(html); w.document.close(); w.focus()
  setTimeout(() => { try { w.print() } catch { /* the reader can print by hand */ } }, 350)
}
